import {
  currentStreak,
  DISPOSED,
  type IsoDate,
  type LarderEvent,
  RESOLVED,
  todayIso,
} from "@larder/shared";
import { useEffect, useMemo, useState } from "react";
import { type TrackedItem, watchLarder } from "./items";

/**
 * Everything the screens need about one household, recomputed from the log.
 *
 * The buckets here ARE the product: "use soon" is the nag, "overdue" is the
 * disposal list, and the streak is the reward. All three are views over the
 * same derivation, so they can never disagree with each other.
 */
export interface LarderState {
  status: "loading" | "ready" | "error";
  error: string | null;
  today: IsoDate;
  items: TrackedItem[];
  events: LarderEvent[];

  /** Needs an answer now: inside the check-in window, unresolved. */
  needsAnswer: TrackedItem[];
  /** Fine for now, dated. Soonest first. */
  active: TrackedItem[];
  /** No date at all — salt, shampoo. Not a problem, just not tracked by date. */
  undated: TrackedItem[];
  /**
   * Items the app PRESUMED went off, never told. These still need a yes or no.
   *
   * Split out from `disposed` when the Binned tab went away. Confirmed waste
   * needs no screen — you already told us, and the event log has it. A guess
   * does: if the app can presume something wasted and you can never say
   * otherwise, the waste number is unfalsifiable, and an unfalsifiable number
   * is not one to build reporting on.
   */
  presumed: TrackedItem[];
  /**
   * Eaten. The win, and for a long time the only bucket with nowhere to be
   * shown — an item answered "ate it" fell out of every list and looked
   * deleted. Most recent first.
   */
  consumed: TrackedItem[];

  streak: number;
  /** Day -> what happened, for the calendar grid. */
  wasteDays: Set<IsoDate>;
  activityDays: Set<IsoDate>;
}

export function useLarder(locationId: string | null, timezone: string): LarderState {
  const [items, setItems] = useState<TrackedItem[]>([]);
  const [events, setEvents] = useState<LarderEvent[]>([]);
  const [status, setStatus] = useState<LarderState["status"]>("loading");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!locationId) return;
    setStatus("loading");
    setError(null);

    const stop = watchLarder(
      locationId,
      timezone,
      (nextItems, nextEvents) => {
        setItems(nextItems);
        setEvents(nextEvents);
        setStatus("ready");
      },
      (e) => {
        setError(e.message);
        setStatus("error");
      },
    );
    return stop;
    // `timezone` is part of the derivation, so a change must re-subscribe.
  }, [locationId, timezone]);

  return useMemo(() => {
    const today = todayIso(timezone);

    const needsAnswer = items
      .filter((i) => i.derived.status === "awaiting")
      .sort(byDaysLeft);
    const active = items.filter((i) => i.derived.status === "active").sort(byDaysLeft);
    const undated = items.filter((i) => i.derived.status === "undated");
    const byResolvedAt = (a: TrackedItem, b: TrackedItem) =>
      (b.derived.resolvedAt ?? 0) - (a.derived.resolvedAt ?? 0);

    const presumed = items
      // Most recent first: a wrong guess is most correctable while it is fresh.
      .filter((i) => i.derived.status === "presumed_wasted")
      .sort(byResolvedAt);
    const consumed = items
      .filter((i) => i.derived.status === "consumed")
      .sort(byResolvedAt);

    // Streak days come from RESOLVED items only. An item still sitting in the
    // fridge is not evidence of anything yet.
    const wasteDays = new Set<IsoDate>();
    const activityDays = new Set<IsoDate>();
    for (const item of items) {
      if (!RESOLVED.has(item.derived.status)) continue;
      // A derived (not yet swept) presumption has no event, so fall back to the
      // day it actually lapsed — otherwise it would silently miss the calendar.
      const day = item.derived.resolvedOn ?? item.derived.effectiveExpiry ?? today;
      activityDays.add(day);
      if (DISPOSED.has(item.derived.status)) wasteDays.add(day);
    }

    return {
      status,
      error,
      today,
      items,
      events,
      needsAnswer,
      active,
      undated,
      presumed,
      consumed,
      streak: currentStreak(wasteDays, activityDays, today),
      wasteDays,
      activityDays,
    };
  }, [items, events, status, error, timezone]);
}

const byDaysLeft = (a: TrackedItem, b: TrackedItem) =>
  (a.derived.daysLeft ?? 1e9) - (b.derived.daysLeft ?? 1e9);
